import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Divider,
  Paper,
  Autocomplete,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
  SnackbarCloseReason,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import SearchIcon from '@mui/icons-material/Search';
import dayjs, { Dayjs } from 'dayjs';
import { useAppDispatch, useAppSelector } from 'reduxStore/hooks';
import { searchPatientDetailsByDocumentId } from 'reduxStore/searchPatientByMRN/searchPatientByDocumentIdSlice';
import { Result } from 'reduxStore/searchPatientByMRN/Model';
import { rfIdList } from 'reduxStore/rfIdList/rfIdListSlice';
import { AddReVisitPayload } from 'reduxStore/addReVisit/Model';
import { addReVisit, resetReVisitState } from 'reduxStore/addReVisit/addReVisitSlice';
import { useNavigate } from 'react-router-dom';

interface RfidOption {
  label: string;
  value: string;
}

interface VisitFormState {
  patientName: string;
  tokenNumber: string;
  department: string;
  inTime: Dayjs | null;
  outTime: Dayjs | null;
  phoneNumber: string;
  patientIdCard: string;
  mrnNumber: string;
  guardian1Name: string;
  guardian1Id: string;
  guardian1Rfid: string;
  guardian2Name: string;
  guardian2Id: string;
  guardian2Rfid: string;
  visitType: string | null;
}

type PatientList = {
  label: string;
  value: number;
};

const Revisit: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [rfidOptions, setRfidOptions] = useState<RfidOption[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const rfIdListResponse = useAppSelector(
    (state) => state.rfIdListRes);
  const addReVisitResponse = useAppSelector(
    (state) => state.addReVisitRes);
  const departmentOptions = [
    'Blood Bank',
    'Pediatric Cardiology',
    'Pediatric Cardiac Surgery',
    'Mother & Child'
  ];

  useEffect(() => {
    if (rfIdListResponse?.response?.data) {
      const rfidOptions = rfIdListResponse?.response?.data.map(item => ({
        label: item.item_code,
        value: item.serial_no
      }));
      setRfidOptions(rfidOptions);
    }
  }, [rfIdListResponse]);

  useEffect(() => {
    return () => {
      dispatch(resetReVisitState());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(rfIdList({ doctype: 'Serial No' }));
  }, [dispatch]);

  const [patientDocumentId, setPatientDocumentId] = useState('');
  const searchPatientDetailsByDocumentIdResponse = useAppSelector(
    (state: any) => state.searchPatientDetailsByDocumentIdRes,
  );
  useEffect(() => {
    const results = searchPatientDetailsByDocumentIdResponse?.response?.results;
    if (results !== undefined && results.length > 0) {
      setPatientDetailsList(results);
      setPatientList(
        results.map((item: Result) => {
          const [value, label] = item.display.split(' - ');
          return {
            label: label,
            value: value,
          };
        }),
      );
    } else if (searchPatientDetailsByDocumentIdResponse?.response !== undefined) {
      setPatientDetailsList([]);
      setPatientList([]);
    }
  }, [searchPatientDetailsByDocumentIdResponse?.response]);

  const [patientList, setPatientList] = useState([] as PatientList[]);
  const [patientDetailsList, setPatientDetailsList] = useState([] as Result[]);
  const [selectedPatient, setSelectedPatient] = useState<PatientList | null>(null);

  const changeSelectedPatient = (passedVal: PatientList | null) => {
    setSelectedPatient(passedVal);
    if (passedVal) {
      const patientDetails = patientDetailsList.find(
        (patient: Result) => {
          const [mrn] = patient.display.split(' - ');
          return mrn === passedVal.value.toString();
        }
      );

      if (patientDetails) {
        const [mrn, patientName] = patientDetails.display.split(' - ');

        setForm({
          ...form,
          patientName: patientName || '',
          mrnNumber: mrn || '',
        });
      }
    } else {
      setForm({
        ...form,
        patientName: '',
        mrnNumber: '',
      });
    }
  };

  const [form, setForm] = useState<VisitFormState>({
    patientName: '',
    tokenNumber: '',
    department: '',
    inTime: dayjs(),
    outTime: null,
    phoneNumber: '',
    patientIdCard: '',
    mrnNumber: '',
    guardian1Name: '',
    guardian1Id: '',
    guardian1Rfid: '',
    guardian2Name: '',
    guardian2Id: '',
    guardian2Rfid: '',
    visitType: 'New_Visit',
  });

  const handleChange =
    (field: keyof VisitFormState) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setForm({ ...form, [field]: event.target.value });
      };

  const handleMrnSearch = () => {
    dispatch(
      searchPatientDetailsByDocumentId({ documentIDNumber: patientDocumentId }),
    );
  };

  const handlePreview = (e: React.FormEvent) => {
    e.preventDefault();
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
  };

  const handleFinalSubmit = () => {
    /* eslint-disable camelcase */
    const body = {
      data: {
        visitor_name: form.patientName,
        token_number: form.tokenNumber,
        department: form.department,
        in_time: dayjs(form.inTime).format('YYYY-MM-DD HH:mm:ss'),
        phone_number: form.phoneNumber,
        mrn_number: form.mrnNumber,
        patient_id_card_number: form.patientIdCard,
        father__husband__guardian_1: form.guardian1Name,
        guardian_1_id_card_number: form.guardian1Id,
        guardian_1_rfid_number: form.guardian1Rfid,
        patient_type: form.visitType ?? 'New_Visit',
        mother__guardian_2: form.guardian2Name,
        guardian_2_rfid_number: form.guardian2Rfid,
        guardian_2_id_card_number: form.guardian2Id,
        doctype: 'Token' as const,
      }
    };
    const access = '3703265d357a211:5b86c8948ff269d';
    const payload: AddReVisitPayload = {
      body,
      access,
    };

    dispatch(addReVisit(payload));
    setPreviewOpen(false);
    navigate('/tokenlist');
    dispatch(resetReVisitState());
  };

  const [open, setOpen] = React.useState(false);
  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };

  // Helper function to get RFID label from value
  const getRfidLabel = (value: string) => {
    const option = rfidOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  return (
    <>
      <Paper
        elevation={2}
        sx={{
          maxWidth: 1400,
          mx: { xs: 1, sm: 2, md: 'auto' },
          my: { xs: 2, sm: 3, md: 4 },
        }}
        variant='outlined'
      >
        <Box
          component="form"
          onSubmit={handlePreview}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
          }}
        >
          {/* Patient Information Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'flex-start',
              alignItems: { xs: 'stretch', sm: 'center' },
              mb: 3,
              gap: 2
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              Patient Information
            </Typography>
            <TextField
              label="MRN Number / Aadhar Number / Phone Number"
              value={patientDocumentId}
              onChange={(event) => setPatientDocumentId(event.target.value)}
              onKeyDown={(event: React.KeyboardEvent) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  dispatch(
                    searchPatientDetailsByDocumentId({ documentIDNumber: patientDocumentId }),
                  );
                }
              }}
              size="small"
              fullWidth
              sx={{
                maxWidth: { sm: 650 }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleMrnSearch}
                      edge="end"
                      color="primary"
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {searchPatientDetailsByDocumentIdResponse?.response !== undefined &&
              searchPatientDetailsByDocumentIdResponse?.response?.results !== undefined &&
              searchPatientDetailsByDocumentIdResponse?.response?.results.length > 0 && (
                <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                  <Autocomplete
                    fullWidth
                    size='small'
                    value={selectedPatient}
                    options={patientList}
                    onChange={(_event: any, newValue: any) => {
                      changeSelectedPatient(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label='Select Patient' variant='outlined' />
                    )}
                    getOptionLabel={(option) => `${option.label} - MRN: ${option.value}`}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <Typography variant="body1">{option.label}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                            MRN: {option.value}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    isOptionEqualToValue={(option: any, value) => option.value === value.value}
                  />
                </Grid>
              )}
          </Box>

          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                required
                label="Patient Name"
                value={form.patientName}
                onChange={handleChange('patientName')}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                freeSolo
                options={departmentOptions}
                value={form.department}
                onChange={(_event, newValue) => {
                  setForm({ ...form, department: newValue || '' });
                }}
                onInputChange={(_event, newInputValue) => {
                  setForm({ ...form, department: newInputValue });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Department"
                    size="small"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                required
                label="Token Number"
                value={form.tokenNumber}
                onChange={handleChange('tokenNumber')}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                required
                label="Phone Number"
                value={form.phoneNumber}
                onChange={handleChange('phoneNumber')}
                size="small"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: { xs: 3, md: 4 } }} />

          {/* Visit Timing Section */}
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              color: 'primary.main',
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}
          >
            Visit Timing
          </Typography>

          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <DateTimePicker
                label="IN Time *"
                value={form.inTime}
                onChange={(val) => setForm({ ...form, inTime: val })}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    required: true
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <DateTimePicker
                label="OUT Time"
                value={form.outTime}
                onChange={(val) => setForm({ ...form, outTime: val })}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: { xs: 3, md: 4 } }} />

          {/* Guardian 1 Information Section */}
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              color: 'primary.main',
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}
          >
            Guardian 1 Information
          </Typography>

          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                required
                label="Father / Husband / Guardian 1 Name"
                value={form.guardian1Name}
                onChange={handleChange('guardian1Name')}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Guardian 1 ID Card Number"
                value={form.guardian1Id}
                onChange={handleChange('guardian1Id')}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={rfidOptions}
                value={rfidOptions.find(option => option.value === form.guardian1Rfid) || null}
                onChange={(_event, newValue) => {
                  setForm({ ...form, guardian1Rfid: newValue?.value || '' });
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Guardian 1 RFID Number"
                    size="small"
                  />
                )}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: { xs: 3, md: 4 } }} />

          {/* Guardian 2 Information Section */}
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              color: 'primary.main',
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}
          >
            Guardian 2 Information (Optional)
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Mother / Guardian 2 Name"
                value={form.guardian2Name}
                onChange={handleChange('guardian2Name')}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Guardian 2 ID Card Number"
                value={form.guardian2Id}
                onChange={handleChange('guardian2Id')}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={rfidOptions}
                value={rfidOptions.find(option => option.value === form.guardian2Rfid) || null}
                onChange={(_event, newValue) => {
                  setForm({ ...form, guardian2Rfid: newValue?.value || '' });
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Guardian 2 RFID Number"
                    size="small"
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'flex-end',
              gap: 2,
              mt: 4,
              pt: 3,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Button
              variant="outlined"
              size="large"
              fullWidth={true}
              sx={{
                minWidth: { sm: 120 },
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              size="large"
              fullWidth={true}
              sx={{
                minWidth: { sm: 120 },
                width: { xs: '100%', sm: 'auto' },
                bgcolor: 'success.main',
                '&:hover': {
                  bgcolor: 'success.dark',
                }
              }}
            >
              Preview
            </Button>
          </Box>

          {addReVisitResponse?.response?.data !== undefined && (
            <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
              <Alert
                onClose={handleClose}
                severity="success"
                variant="filled"
                sx={{ width: '100%' }}
              >
                Revisit Added Successfully!
              </Alert>
            </Snackbar>
          )}
        </Box>
      </Paper>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            bgcolor: 'success.main',
            color: 'white',
            fontWeight: 600
          }}
        >
          Preview Visit Details
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {/* Patient Information */}
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
            Patient Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Patient Name</Typography>
              <Typography variant="body1" fontWeight={500}>{form.patientName}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Department</Typography>
              <Typography variant="body1" fontWeight={500}>{form.department}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Token Number</Typography>
              <Typography variant="body1" fontWeight={500}>{form.tokenNumber}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Phone Number</Typography>
              <Typography variant="body1" fontWeight={500}>{form.phoneNumber}</Typography>
            </Grid>
            {form.patientIdCard && (
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Patient ID Card Number</Typography>
                <Typography variant="body1" fontWeight={500}>{form.patientIdCard}</Typography>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Visit Timing */}
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
            Visit Timing
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">IN Time</Typography>
              <Typography variant="body1" fontWeight={500}>
                {form.inTime ? dayjs(form.inTime).format('DD/MM/YYYY hh:mm A') : '-'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">OUT Time</Typography>
              <Typography variant="body1" fontWeight={500}>
                {form.outTime ? dayjs(form.outTime).format('DD/MM/YYYY hh:mm A') : '-'}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Guardian 1 Information */}
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
            Guardian 1 Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Guardian 1 Name</Typography>
              <Typography variant="body1" fontWeight={500}>{form.guardian1Name}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Guardian 1 ID Card</Typography>
              <Typography variant="body1" fontWeight={500}>{form.guardian1Id || '-'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Guardian 1 RFID</Typography>
              <Typography variant="body1" fontWeight={500}>
                {getRfidLabel(form.guardian1Rfid) || '-'}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Guardian 2 Information */}
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
            Guardian 2 Information
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Guardian 2 Name</Typography>
                <Typography variant="body1" fontWeight={500}>{form.guardian2Name || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Guardian 2 ID Card</Typography>
                <Typography variant="body1" fontWeight={500}>{form.guardian2Id || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Guardian 2 RFID</Typography>
                <Typography variant="body1" fontWeight={500}>
                  {getRfidLabel(form.guardian2Rfid) || '-'}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleClosePreview} variant="outlined" size="large">
            Cancel
          </Button>
          <Button
            onClick={handleFinalSubmit}
            variant="contained"
            size="large"
            sx={{
              bgcolor: 'success.main',
              '&:hover': {
                bgcolor: 'success.dark',
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Revisit;